/* eslint-disable no-param-reassign */
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { RootState } from '../storeSetup';
import { MembershipUpgradeError, upgradeMembership } from '../thunks/subscriptionThunk';

type UpgradeStatus = 'idle' | 'loading' | 'success' | 'error';

interface SubscriptionState {
  upgradeStatus: UpgradeStatus;
  upgradeError: string | null;
  upgradeErrorCode: string | null;
  membershipPlan: 'edge' | 'srt';
  needsOnboarding: boolean;
}

const initialState: SubscriptionState = {
  upgradeStatus: 'idle',
  upgradeError: null,
  upgradeErrorCode: null,
  membershipPlan: 'edge',
  needsOnboarding: false,
};

export const SubscriptionSlice = createSlice({
  name: 'subscription',
  initialState,
  reducers: {
    resetUpgradeStatus: (state) => {
      state.upgradeStatus = 'idle';
      state.upgradeError = null;
      state.upgradeErrorCode = null;
    },
    dismissOnboarding: (state) => {
      state.needsOnboarding = false;
    },
    setMembershipPlan: (state, action: PayloadAction<'edge' | 'srt'>) => {
      state.membershipPlan = action.payload;
    },
  },
  extraReducers(builder) {
    builder
      .addCase(upgradeMembership.pending, (state) => {
        state.upgradeStatus = 'loading';
        state.upgradeError = null;
        state.upgradeErrorCode = null;
      })
      .addCase(upgradeMembership.fulfilled, (state) => {
        state.upgradeStatus = 'success';
        state.upgradeError = null;
        state.upgradeErrorCode = null;
        state.membershipPlan = 'srt';
        state.needsOnboarding = true;
      })
      .addCase(upgradeMembership.rejected, (state, action) => {
        state.upgradeStatus = 'error';
        const payload = action.payload as MembershipUpgradeError | undefined;
        state.upgradeError = payload?.message || 'An unexpected error occurred. Please try again.';
        state.upgradeErrorCode = payload?.code || null;
      });
  },
});

export const {
  resetUpgradeStatus,
  dismissOnboarding,
  setMembershipPlan,
} = SubscriptionSlice.actions;

export const selectUpgradeStatus = (state: RootState) => state.subscription.upgradeStatus;
export const selectUpgradeError = (state: RootState) => state.subscription.upgradeError;
export const selectUpgradeErrorCode = (state: RootState) => state.subscription.upgradeErrorCode;
export const selectMembershipPlan = (state: RootState) => state.subscription.membershipPlan;
export const selectNeedsOnboarding = (state: RootState) => state.subscription.needsOnboarding;

export default SubscriptionSlice.reducer;
